import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
  ChainProvider,
  buildChainInitialState,
  chainReducer,
  useChain,
} from '../chainState'
import { ChainSimulationRunner } from './ChainSimulationRunner'
import { SubmitBar } from './SubmitBar'
import { useSaveProgress } from '../useSaveProgress'
import { client } from '@Utils'
import { simulationEndpoints } from '@URLS'

/**
 * Inner component rendered inside the ChainProvider so it can
 * call useChain() and wire up persistence.
 */
const ChainInner = ({ activityPk, coursenum, completedResults, canRetry }) => {
  const [chainState] = useChain()

  // Auto-save (no-ops when activityPk is falsy)
  // For chains, we save the chainState; state comes from the active sim's inner component
  useSaveProgress(null, chainState, activityPk, coursenum)

  return (
    <>
      {!completedResults && (
        <div className={`simulation-chain${chainState.chainComplete && chainState.reviewIndex == null ? ' simulation-complete' : ''}`}>
          <ChainSimulationRunner />
        </div>
      )}
      {activityPk && (
        <SubmitBar
          activityPk={activityPk}
          coursenum={coursenum}
          state={null}
          chainState={chainState}
          completedResults={completedResults}
          canRetry={canRetry}
        />
      )}
    </>
  )
}

ChainInner.propTypes = {
  activityPk: PropTypes.string,
  coursenum: PropTypes.string,
  completedResults: PropTypes.object,
  canRetry: PropTypes.bool,
}

export const SimulationChainApp = ({ config }) => {
  const activityPk = config.activityPk || null
  const coursenum = config.coursenum || ''
  const [initialState, setInitialState] = useState(null)
  const [completedResults, setCompletedResults] = useState(null)
  const [canRetry, setCanRetry] = useState(false)
  const [loading, setLoading] = useState(!!activityPk)

  useEffect(() => {
    if (!activityPk) {
      setInitialState(buildChainInitialState(config))
      return
    }

    // Load saved progress from backend
    client
      .post(simulationEndpoints.loadProgress({ coursenum, activityPk }))
      .then(res => {
        const saved = res.data
        if (saved && saved.chainState && Object.keys(saved.chainState).length > 0 && saved.status !== 'completed') {
          // Restore from saved chain state
          setInitialState(saved.chainState)
        } else {
          setInitialState(buildChainInitialState(config))
        }
        // Capture completed results for display
        if (saved && saved.completedResults) {
          setCompletedResults(saved.completedResults)
        }
        if (saved && saved.canRetry) {
          setCanRetry(true)
        }
      })
      .catch(() => {
        setInitialState(buildChainInitialState(config))
      })
      .finally(() => {
        setLoading(false)
      })
  }, [])

  if (loading || !initialState) {
    return <div className="simulation-chain"><p>Loading...</p></div>
  }

  return (
    <ChainProvider initialState={initialState} reducer={chainReducer}>
      <ChainInner activityPk={activityPk} coursenum={coursenum} completedResults={completedResults} canRetry={canRetry} />
    </ChainProvider>
  )
}

SimulationChainApp.propTypes = {
  config: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    simulations: PropTypes.arrayOf(
      PropTypes.shape({
        guid: PropTypes.string,
        title: PropTypes.string,
        plugin: PropTypes.string,
        pluginConfig: PropTypes.object,
        tasks: PropTypes.array,
        statePolicy: PropTypes.string,
      })
    ),
    activityPk: PropTypes.string,
    coursenum: PropTypes.string,
  }).isRequired,
}
